import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OccurrencesHeader from "../../components/occurrences/OccurrencesHeader";
import StudentService from "../../services/StudentService";
import { AbilityContext } from "../../acl/can";
import BackButton from "../../components/BackButton";
import OccurrencesPage from "../../components/occurrences/OccurrencesPage";
import OccurrenceTypeFilters, {
  ALL_REASONS_ID
} from "../../components/occurrences/OccurrenceTypeFilters";
import useOccurrenceTypeFilters from "../../components/occurrences/useOccurrenceTypeFilters";
import { isIndividualOccurrenceJob } from "../../utils/jobHelpers";
import withOccurrenceAdvanced from "../../utils/withOccurrenceAdvanced";
import {
  canCreateOccurrence,
  lockedGroupIdsFromLocation
} from "../../utils/occurrenceAdvanced";
import useCreatableStudentClasses from "./useCreatableStudentClasses";

const toReasonCode = reasonId =>
  reasonId === ALL_REASONS_ID ? undefined : Number(reasonId.split("-")[0]);

function StudentOccurrences({
  match,
  location,
  occurrenceAdvanced,
  occurrenceAdvancedAcl
}) {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  const { studentId: routeStudentId } = match.params;
  const classId = match.params.id || null;
  const lockedGroupsKey = (lockedGroupIdsFromLocation(location) || []).join(
    ","
  );
  const allowedGroupIds = useMemo(
    () => (lockedGroupsKey ? lockedGroupsKey.split(",") : null),
    [lockedGroupsKey]
  );

  const [student, setStudent] = useState(null);
  const typeFilters = useOccurrenceTypeFilters(classId, allowedGroupIds);
  const { selectedGroup, reasonId } = typeFilters;

  useEffect(() => {
    StudentService.getStudentById(routeStudentId).then(setStudent);
  }, [routeStudentId]);

  const studentId = student && student.id != null ? student.id : routeStudentId;
  const classes = useCreatableStudentClasses(
    routeStudentId,
    !classId,
    occurrenceAdvanced
  );
  const groupCode =
    selectedGroup && selectedGroup.idGroup
      ? Number(selectedGroup.idGroup)
      : undefined;
  const reasonCode = toReasonCode(reasonId);

  const fetchOccurrences = useCallback(
    year =>
      StudentService.getOccurrences(studentId, year, groupCode, reasonCode),
    [studentId, groupCode, reasonCode]
  );

  const menuGroupFilter = useMemo(() => {
    if (!allowedGroupIds) {
      return null;
    }
    const allowed = new Set(allowedGroupIds);
    return occurrence => allowed.has(String(occurrence.idGroup));
  }, [allowedGroupIds]);

  const matchesJob = useCallback(
    job => isIndividualOccurrenceJob(job, classId, studentId),
    [classId, studentId]
  );

  if (!student || (allowedGroupIds && !typeFilters.loaded)) {
    return (
      <Box p={6} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (allowedGroupIds && !typeFilters.groups.length) {
    return (
      <Grid container spacing={2}>
        <BackButton to={`/student/${studentId}`} />
        <Grid item xs={12}>
          <Typography>{t("occurrences.groupMenu.unavailable")}</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <OccurrencesPage
      classId={classId}
      backTo={`/student/${studentId}`}
      header={(
        <OccurrencesHeader
          name={student.name}
          info={
            allowedGroupIds
              ? t("occurrences.groupMenu.info", {
                  label: typeFilters.groups.map(group => group.name).join(", "),
                  ra: student.ra
                })
              : t("occurrences.ra", { ra: student.ra })
          }
          photo={student.photo}
        />
      )}
      filters={<OccurrenceTypeFilters {...typeFilters} />}
      fetchOccurrences={fetchOccurrences}
      occurrenceFilter={menuGroupFilter}
      matchesJob={matchesJob}
      advanced={occurrenceAdvanced}
      searchScope="student"
      canView={!occurrenceAdvanced || occurrenceAdvancedAcl.view}
      canCreate={canCreateOccurrence(ability, occurrenceAdvanced)}
      addLabel={
        occurrenceAdvanced
          ? t("occurrences.advanced.add")
          : t("occurrences.student.add")
      }
      studentId={String(student.id)}
      studentName={student.name}
      classes={classes}
      allowedGroupIds={allowedGroupIds}
    />
  );
}

StudentOccurrences.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string)
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    state: PropTypes.shape({
      group: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array
      ])
    })
  }).isRequired,
  occurrenceAdvanced: PropTypes.bool,
  occurrenceAdvancedAcl: PropTypes.shape({
    view: PropTypes.bool,
    create: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool
  })
};

StudentOccurrences.defaultProps = {
  occurrenceAdvanced: false,
  occurrenceAdvancedAcl: {
    view: false,
    create: false,
    edit: false,
    delete: false
  }
};

export default withOccurrenceAdvanced(withRouter(StudentOccurrences));
